import { useState } from "preact/hooks";
import type { RoutableProps } from "preact-router";
import { fetchDisputeForEvidence, submitDisputeEvidence } from "../api";
import { BrandHeader } from "../components/brandHeader";
import { PrimaryButton } from "../components/buttons";
import { LegalFooter } from "../components/footer";
import { HoneypotInput } from "../components/honeypotInput";
import { Textarea } from "../components/inputs";
import { STRINGS } from "../constants/strings";
import { useResourceLoad } from "../hooks/useResourceLoad";
import { FREITEXT_MAX } from "../state/formState";
import type { GetDisputeForEvidenceOk } from "../types";

interface Props extends RoutableProps {
  evidenceToken?: string;
}

function formatDeadline(deadline: number | null): string | null {
  if (!deadline) return null;
  return new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(deadline));
}

export function DisputeEvidenceScreen({ evidenceToken }: Props) {
  const { data: dispute, loadError } = useResourceLoad(
    evidenceToken,
    fetchDisputeForEvidence
  );

  if (!dispute || !evidenceToken) {
    return (
      <div class="tl-page">
        <div class="tl-page-centered">
          {loadError ? (
            <span class="tl-field__error" role="alert">
              {loadError}
            </span>
          ) : (
            <p>{STRINGS.loading}</p>
          )}
        </div>
      </div>
    );
  }

  return <DisputeForm evidenceToken={evidenceToken} dispute={dispute} />;
}

interface FormProps {
  evidenceToken: string;
  dispute: GetDisputeForEvidenceOk;
}

function DisputeForm({ evidenceToken, dispute }: FormProps) {
  const [text, setText] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const deadline = formatDeadline(dispute.deadline);

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    if (submitting) return;
    if (text.trim() === "") {
      setSubmitError(STRINGS.disputeEmpty);
      return;
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await submitDisputeEvidence({
        evidenceToken,
        text,
        website_url: websiteUrl,
      });
      if (res.status === "ok") {
        setDone(true);
        return;
      }
      if (res.status === "too_long") {
        setSubmitError(STRINGS.disputeTooLong(res.maxLength ?? FREITEXT_MAX));
        return;
      }
      if (res.status === "invalid_input") {
        setSubmitError(STRINGS.disputeEmpty);
        return;
      }
      if (res.status === "rate_limited") {
        setSubmitError(STRINGS.resendRateLimit);
        return;
      }
      setSubmitError(STRINGS.genericError);
    } catch {
      setSubmitError(STRINGS.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div class="tl-page">
        <div
          class="tl-page-centered"
          style="padding: 64px var(--tl-page-pad-x) 48px;"
        >
          <div class="tl-icon-circle tl-icon-circle--lg">
            <span class="material-icons" aria-hidden="true">
              check_circle
            </span>
          </div>
          <h1 class="tl-screen-title tl-screen-title--xl">
            {STRINGS.disputeSuccessTitle}
          </h1>
          <p class="tl-screen-body">{STRINGS.disputeSuccessBody}</p>
        </div>
        <LegalFooter />
      </div>
    );
  }

  return (
    <div class="tl-page">
      <div class="tl-page-centered">
        <BrandHeader
          companyName={dispute.companyName}
          companyLogoUrl={null}
          title={STRINGS.disputeTitle}
          subtitle={STRINGS.disputeIntro(dispute.companyName)}
        />

        <section class="tl-card">
          <h2 class="tl-card__heading">{STRINGS.disputeReviewHeading}</h2>
          {dispute.service && (
            <p class="tl-screen-body">
              {STRINGS.disputeServiceLine(dispute.service)}
            </p>
          )}
          <p class="tl-screen-body">{dispute.freitext}</p>
        </section>

        <form onSubmit={onSubmit}>
          <Textarea
            label={STRINGS.disputeFieldLabel}
            value={text}
            placeholder={STRINGS.disputePlaceholder}
            maxLength={FREITEXT_MAX}
            onInput={setText}
          />
          <HoneypotInput value={websiteUrl} onInput={setWebsiteUrl} />
          {deadline && (
            <p class="tl-screen-body">{STRINGS.disputeDeadline(deadline)}</p>
          )}
          {submitError && (
            <span class="tl-field__error" role="alert">
              {submitError}
            </span>
          )}
          <PrimaryButton
            text={STRINGS.disputeSubmit}
            type="submit"
            loading={submitting}
          />
        </form>
      </div>
      <LegalFooter />
    </div>
  );
}
